import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { activateSubscriber, fetchSubscribers } from "../api/client";
import type { SubscriberSummary } from "../api/types";

export type Role = "ADMIN" | "ORG_MANAGER" | "TEACHER" | "UNKNOWN";
export type UserRole = "ADMIN" | "STANDARD" | "UNKNOWN";

type OrgContextValue = {
  subscribers: SubscriberSummary[];
  activeSubId: number | null;
  activeRole: Role;
  userRole: UserRole;
  isInstitution: boolean;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  setActiveSub: (subId: number) => Promise<void>;
};

const OrgContext = createContext<OrgContextValue | null>(null);

export const OrgProvider = ({ children }: { children: ReactNode }) => {
  const [subscribers, setSubscribers] = useState<SubscriberSummary[]>([]);
  const [activeSubId, setActiveSubId] = useState<number | null>(null);
  const [userRole, setUserRole] = useState<UserRole>("UNKNOWN");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSubscribers();
      setSubscribers(data.subscribers || []);
      setActiveSubId(data.active_subscriber_id || null);
      setUserRole((data.user_role as UserRole) || "STANDARD");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const setActiveSub = async (subId: number) => {
    await activateSubscriber(subId);
    await reload();
  };

  const activeRole: Role = useMemo(() => {
    if (userRole === "ADMIN") return "ADMIN";
    const active = subscribers.find((s) => s.id === activeSubId);
    const r = (active?.membership_role || "").toUpperCase();
    if (r === "ORG_MANAGER") return "ORG_MANAGER";
    if (r === "TEACHER") return "TEACHER";
    return "UNKNOWN";
  }, [userRole, subscribers, activeSubId]);

  const isInstitution = useMemo(() => {
    const active = subscribers.find((s) => s.id === activeSubId);
    return active?.subscriber_type === "INSTITUTION";
  }, [subscribers, activeSubId]);

  const value: OrgContextValue = {
    subscribers,
    activeSubId,
    activeRole,
    userRole,
    isInstitution,
    loading,
    error,
    reload,
    setActiveSub,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
};

export const useOrgContext = (): OrgContextValue => {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrgContext must be used within OrgProvider");
  return ctx;
};
